import {
  CheckboxItem,
  Content,
  Group,
  Item,
  ItemIndicator,
  Label,
  Portal,
  Root,
  Separator,
  Sub,
  SubContent,
  SubTrigger,
  Trigger,
} from '@radix-ui/react-dropdown-menu';
import { Check } from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';

import { cn } from '@/shared/utils/cn/cn.util';

import { POPPER_SCROLLER_CLASSES } from '../scroll/scroll.tokens';

import { dropdownItemVariants, type DropdownItemVariants } from './DropdownMenu.tokens';

export const DropdownMenu = Root;
export const DropdownMenuTrigger = Trigger;
export const DropdownMenuGroup = Group;
export const DropdownMenuSub = Sub;

export type DropdownMenuContentProps = ComponentPropsWithoutRef<typeof Content>;

/**
 * The floating surface, shared by the menu and its sub-menus.
 *
 * `max-h-dropdown-available` is the ceiling: a row-actions menu opened on the
 * last row of a full table, or any menu at all on a short viewport, used to run
 * off the bottom of the screen with its final item — often the destructive one —
 * unreachable. Radix measures the room between the trigger and the viewport edge
 * per open and publishes it as a CSS variable; the preset names it, and the menu
 * scrolls inside whatever it turns out to be that time.
 *
 * The old `overflow-hidden` is gone with it: hiding the overflow of an unbounded
 * box does not contain a menu, it truncates it.
 */
const SURFACE_CLASSES = [
  'z-popper min-w-48 rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-lg',
  'max-h-dropdown-available max-w-dropdown-available',
  POPPER_SCROLLER_CLASSES,
  'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
  'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
].join(' ');

export const DropdownMenuContent = forwardRef<ElementRef<typeof Content>, DropdownMenuContentProps>(
  function DropdownMenuContent({ className, align = 'end', sideOffset = 4, ...props }, ref) {
    return (
      <Portal>
        <Content
          ref={ref}
          // `align="end"` is logical in Radix: a row-actions menu hangs from the
          // inline end of its trigger in both directions.
          align={align}
          sideOffset={sideOffset}
          className={cn(SURFACE_CLASSES, className)}
          {...props}
        />
      </Portal>
    );
  },
);

export interface DropdownMenuItemProps
  extends ComponentPropsWithoutRef<typeof Item>, DropdownItemVariants {}

export const DropdownMenuItem = forwardRef<ElementRef<typeof Item>, DropdownMenuItemProps>(
  function DropdownMenuItem({ className, tone, inset, ...props }, ref) {
    return (
      <Item ref={ref} className={cn(dropdownItemVariants({ tone, inset }), className)} {...props} />
    );
  },
);

export type DropdownMenuCheckboxItemProps = ComponentPropsWithoutRef<typeof CheckboxItem>;

export const DropdownMenuCheckboxItem = forwardRef<
  ElementRef<typeof CheckboxItem>,
  DropdownMenuCheckboxItemProps
>(function DropdownMenuCheckboxItem({ className, children, ...props }, ref) {
  return (
    <CheckboxItem
      ref={ref}
      className={cn(dropdownItemVariants({ inset: true }), className)}
      {...props}
    >
      <span className="absolute start-sm inline-flex h-4 w-4 items-center justify-center">
        <ItemIndicator>
          <Check aria-hidden className="h-4 w-4" />
        </ItemIndicator>
      </span>
      {children}
    </CheckboxItem>
  );
});

export type DropdownMenuLabelProps = ComponentPropsWithoutRef<typeof Label>;

export const DropdownMenuLabel = forwardRef<ElementRef<typeof Label>, DropdownMenuLabelProps>(
  function DropdownMenuLabel({ className, ...props }, ref) {
    return (
      <Label
        ref={ref}
        className={cn('px-md py-1.5 text-caption font-medium text-muted-foreground', className)}
        {...props}
      />
    );
  },
);

export type DropdownMenuSeparatorProps = ComponentPropsWithoutRef<typeof Separator>;

export const DropdownMenuSeparator = forwardRef<
  ElementRef<typeof Separator>,
  DropdownMenuSeparatorProps
>(function DropdownMenuSeparator({ className, ...props }, ref) {
  return <Separator ref={ref} className={cn('-mx-1 my-1 h-px bg-border', className)} {...props} />;
});

export type DropdownMenuSubTriggerProps = ComponentPropsWithoutRef<typeof SubTrigger>;

export const DropdownMenuSubTrigger = forwardRef<
  ElementRef<typeof SubTrigger>,
  DropdownMenuSubTriggerProps
>(function DropdownMenuSubTrigger({ className, ...props }, ref) {
  return (
    <SubTrigger
      ref={ref}
      className={cn(dropdownItemVariants(), 'data-[state=open]:bg-secondary', className)}
      {...props}
    />
  );
});

export type DropdownMenuSubContentProps = ComponentPropsWithoutRef<typeof SubContent>;

export const DropdownMenuSubContent = forwardRef<
  ElementRef<typeof SubContent>,
  DropdownMenuSubContentProps
>(function DropdownMenuSubContent({ className, ...props }, ref) {
  return <SubContent ref={ref} className={cn(SURFACE_CLASSES, className)} {...props} />;
});
